import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Modal from '../../common/Modal.js';
import DeliverySLA from './DeliverySLA';
import { mapSlaOptions } from './mapSlaResponse';
import { hideModal } from '../../../actions/shared/modal';
import { selectSize as selectPdpSize } from '../../../actions/productData';
import { addToCart } from '../../../actions/addProductToCart';
import { ModalTypes } from '../../../constants/index.js';
import { getB2cSellerConfig } from '../../../refresh/selectors/featureFlagsSelector';
import {
  appendItmSourceToURL,
  segmentEventPush,
  getProductItemPDP,
  getSelectSizeSlaEventProps,
  pushDeliveryReturnDetailsViewedB2c,
  pushAllSellersScreenViewed,
  pushSellerOptionsAvailableB2c,
  pushAddToBagClickedB2cAllSellers
} from '../../../modules/ga/GTMdatalayer';
import {
  SLA_SURFACE,
  SlaSurface,
  getSlaSurfaceScreenNames
} from '../../../refresh/molecules/DeliverySLA/constants';
import useDeliveryDetailsViewed from '../../../refresh/molecules/DeliverySLA/useDeliveryDetailsViewed';
import { getProductData } from '../../../refresh/selectors/productpage';
import properties from '../../../modules/ajio-util/properties';
import {
  fetchDeliveryDetailsV2,
  fetchSellerListSla,
  resetAddedToCart
} from '../../../edd/actions';
import {
  selectIsSellerListFetching,
  selectSellerListError,
  selectSellerListOptions
} from './selectSla';
// TODO - Remove this mock data used
import { B2C_MOCK_MODAL_TITLE } from './b2cSellerMockData';
import { PriceTypeTags, RawSlaOption, SlaCodeLabels, SlaOption } from './types';
import styles from './index.pcss';

/** One selectable size in the header strip. */
export interface SizeChip {
  code: string;
  label: string;
  outOfStock: boolean;
}

/**
 * Product the modal describes, when the host supplies it instead of letting the
 * modal read the PDP's `product` slice. The desktop cart / wishlist Size-Quantity
 * popup is the only host that does: it works off its own cart line / wishlist
 * entity, and `product.productDetails` there holds whatever PDP was last visited.
 */
export interface B2cSellerModalProduct {
  brandName?: string;
  productName?: string;
  imageUrl?: string;
  /** aggregator option code — scopedView needs it alongside the sku */
  optionCode?: string;
  sizes?: SizeChip[];
  selectedSizeCode?: string;
  /** Quantity the host opened with, for hosts that own one (the cart line). */
  quantity?: number;
  /**
   * The line this modal was opened for, for "Delivery And Return Details
   * Viewed". Off the PDP the product slice holds whatever PDP was last visited,
   * so bag / wishlist hosts describe their own line with this.
   */
  productItem?: Record<string, unknown> | null;
  /** Host size state, for the event's size_selected. */
  sizeName?: string;
}

interface StateProps {
  /** PDP product slice, for the "Select Size Clicked" products payload. */
  productData?: any;
  brandName: string;
  productName: string;
  imageUrl: string;
  /** aggregator option code — scopedView needs it alongside the sku */
  optionCode: string;
  sizes: SizeChip[];
  selectedSizeCode: string;
  activePincode?: string;
  priceTypeTags?: PriceTypeTags;
  /** SLA-code → copy map — b2cSellerConfig.slaTimeMessage. */
  slaCodeLabels?: SlaCodeLabels;
  /** Every seller for the selected size (scopedView, fetchAllSellers=true). */
  sellerListOptions?: RawSlaOption[];
  isSellerListFetching?: boolean;
  sellerListError?: boolean;
  /** modal open — mirrors what Modal itself reads out of `state.modals` */
  isOpen: boolean;
  /** PDP already added this product — the CTA becomes "Go to Bag", as it does there. */
  addedToCart?: boolean;
  selectedSizeName?: string;
  /** All SKUs cached for the pincode, for is_multi_seller_enabled_size. */
  slaDetailsBySku?: Record<string, any> | null;
}

interface DispatchProps {
  close: () => void;
  fetchSellerList: (sizeCode: string, optionCode: string) => void;
  /** Re-scope the PDP card to one seller (fetchAllSellers=false + its ids). */
  refetchSelectedSeller: (
    sizeCode: string,
    optionCode: string,
    sellerScope: { priceGroupId?: string; sellerId?: string; priceType?: string }
  ) => void;
  selectSize: (sizeName: string, sizeCode: string, optionCode: string) => void;
  addProductToCart: (sizeCode: string, selectedPriceType: string, sellerId: string) => void;
  /** Puts the CTA back to "Add to Bag" once the picked seller / SLA changes. */
  clearAddedToCart: () => void;
}

interface OwnProps {
  /** luxe theme, forwarded from the desktop PDP (newDesignLuxe) */
  luxe?: boolean;
  /**
   * Product to describe, overriding every store read below. Hosts other than
   * the PDP pass their own line item; the PDP omits it.
   */
  product?: B2cSellerModalProduct | null;
  /** Size picked in the strip. Replaces the PDP's `selectSize` dispatch. */
  onSelectSize?: (sizeCode: string, sizeLabel: string) => void;
  /**
   * "Add to Bag". Replaces the PDP's `addToCart` dispatch. Carries the seller
   * behind the picked card as well — the B2C cart API needs it.
   *
   * `pickedOption` is that card's raw API option. The host's own flow fires
   * "Add To Bag Clicked", and without the option it has nothing to report the
   * picked seller from: this list is a separate fetchAllSellers response, so the
   * seller is in none of the option arrays the host already holds.
   */
  onAddToBag?: (
    sizeCode: string,
    selectedPriceType: string,
    sellerId?: string,
    pickedOption?: RawSlaOption | null
  ) => void;
  /** Raw options for the seller list, when the host has its own set. */
  slaOptions?: RawSlaOption[];
  /**
   * Modal dismissed. For hosts that mount this outside the component that owns
   * the flow (the wishlist card), which has its own state to clear.
   */
  onClose?: () => void;
  /**
   * Quantity stepper, shown only when the host controls a quantity. The cart
   * does — the line already has one and the modal stands in for the popup that
   * used to edit it. The PDP and the wishlist add a single unit, so they omit
   * both and the block never renders.
   */
  quantity?: number;
  onQuantityChange?: (next: number) => void;
  /** Same bounds the cart popup's own stepper uses. */
  minQuantity?: number;
  maxQuantity?: number;
  /** CTA copy, for hosts not adding to the bag — the cart updates a line. */
  ctaLabel?: string;
  /**
   * Surface that opened this list, so "Delivery And Return Details Viewed"
   * reports the screen it came from — PDP, bag popup or wishlist popup.
   */
  surface?: SlaSurface;
}

type Props = StateProps & DispatchProps & OwnProps;

/**
 * Surfaces whose All Sellers list reports "Delivery And Return Details Viewed".
 * A host with no `surface` of its own keeps the event off.
 */
const EVENT_SURFACES: SlaSurface[] = [
  SLA_SURFACE.PDP,
  SLA_SURFACE.SIZE_HALF_CARD,
  SLA_SURFACE.BAG_SHEET,
  SLA_SURFACE.WISHLIST_SHEET
];

const SIZE_LABEL_QUALIFIERS = ['standardSize', 'size'];

/** Labels the size radiogroup. Not the PDP's `selectSizeId` — that one is on the
 *  page behind this modal and ids have to stay unique. */
const SIZE_LABEL_ID = 'b2c-seller-size-label';

/** Labels the quantity stepper, for the same reason. */
const QTY_LABEL_ID = 'b2c-seller-qty-label';

/** Display label for a size option — brand/standard size, else the raw value. */
export const sizeLabelOf = (option: any = {}): string => {
  const qualifiers = option?.variantOptionQualifiers || [];
  for (const qualifier of SIZE_LABEL_QUALIFIERS) {
    const match = qualifiers.find((entry: any) => entry?.qualifier === qualifier && entry?.value);
    if (match) return match.value;
  }
  return option?.scDisplaySize || option?.value || '';
};

/**
 * Seller list modal for the desktop PDP (`b2cSellerConfig.isEnabled`), opened
 * from "Other sellers available" on the B2C Delivery SLA card.
 *
 * Composed entirely of what the wishlist / cart size-qty popup already uses:
 * the shared `Modal` shell and the pure `DeliverySLA` list. Only the product
 * header, the size strip and the Add to Bag CTA are local — the cart popup's
 * versions of those carry cart-line state (entryNumber, qty stepper, cart
 * events) that has no meaning on the PDP.
 *
 * Picking a seller is local to the modal until Add to Bag: the card the user
 * chose supplies `selectedPriceType`, the same field `AddToCart` sends for the
 * multiseller PDP.
 */
const B2cSellerModal: FC<Props> = ({
  brandName,
  productName,
  imageUrl,
  optionCode,
  sizes,
  selectedSizeCode,
  activePincode,
  priceTypeTags,
  slaCodeLabels,
  sellerListOptions,
  productData,
  isSellerListFetching,
  sellerListError,
  isOpen,
  addedToCart,
  close,
  fetchSellerList,
  refetchSelectedSeller,
  selectSize,
  addProductToCart,
  clearAddedToCart,
  luxe = false,
  product = null,
  onSelectSize,
  onAddToBag,
  slaOptions,
  onClose,
  quantity,
  onQuantityChange,
  minQuantity = 1,
  maxQuantity = 10,
  ctaLabel,
  surface = SLA_SURFACE.PDP,
  selectedSizeName,
  slaDetailsBySku
}) => {
  const [pickedOption, setPickedOption] = useState<SlaOption | null>(null);

  // A host-supplied product replaces the PDP store reads wholesale — the modal
  // has to describe the line the host opened it for, not the last PDP visited.
  const hostProduct = product || {};
  // Controlled by the host; `product.quantity` is only the value it opened with.
  const pickedQuantity = typeof quantity === 'number' ? quantity : hostProduct.quantity || 0;
  const changeQuantity = onQuantityChange || (() => {});
  const showQuantity = Boolean(onQuantityChange) && pickedQuantity > 0;
  const shownBrandName = product ? hostProduct.brandName || '' : brandName;
  const shownProductName = product ? hostProduct.productName || '' : productName;
  const shownImageUrl = product ? hostProduct.imageUrl || '' : imageUrl;
  const shownOptionCode = product ? hostProduct.optionCode || '' : optionCode;
  const shownSizes = product ? hostProduct.sizes || [] : sizes;
  const shownSelectedSizeCode = product ? hostProduct.selectedSizeCode || '' : selectedSizeCode;
  /**
   * Forget the previous session's pick.
   *
   * Only the `Modal` child unmounts when hidden, so this state outlives the
   * modal — and it wins over the list's own `isSelected`, so reopening and
   * closing without picking anything re-scoped to whatever was chosen the time
   * before. A size change is the same situation: that pick is for another sku.
   */
  useEffect(() => {
    setPickedOption(null);
  }, [isOpen, shownSelectedSizeCode]);
  /** Label of the size the strip is on, for "Delivery And Return Details Viewed". */
  const shownSelectedSizeLabel =
    shownSizes.find(sizeChip => sizeChip?.code === shownSelectedSizeCode)?.label || '';

  /**
   * Freeze the page behind the modal.
   *
   * `showModal` puts `noScroll` on <body>, but the PDP scrolls the documentElement,
   * where a body-level `overflow: hidden` does not reach — so the page kept
   * moving under the panel. Pinning both elements covers whichever one this page
   * scrolls, and the previous inline values are put back on close so nothing is
   * left locked (the class is shared with every other modal).
   */
  useEffect(() => {
    if (!isOpen) return undefined;
    const { documentElement, body } = document;
    const previous = {
      html: documentElement.style.overflow,
      body: body.style.overflow,
      padding: body.style.paddingRight
    };
    // Hiding the overflow takes the page scrollbar away, which would shift the
    // whole PDP sideways as the modal opens; hold its width back as padding.
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;
    documentElement.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      documentElement.style.overflow = previous.html;
      body.style.overflow = previous.body;
      body.style.paddingRight = previous.padding;
    };
  }, [isOpen]);

  // The cart / wishlist popups hand over their own card set; the PDP leaves it
  // to this modal, which asks scopedView for every seller when it opens.
  const hostOwnsOptions = Array.isArray(slaOptions);
  const allRawOptions = hostOwnsOptions ? slaOptions : sellerListOptions;
  // A seller that cannot deliver to this pincode, or is out of stock, has no SLA worth showing.
  const rawOptions = useMemo(
    () =>
      Array.isArray(allRawOptions)
        ? allRawOptions.filter(
            option =>
              option?.serviceabilityDetails?.productDetails?.[0]?.servicability !== false &&
              option?.stock?.stockLevelStatus !== 'outOfStock'
          )
        : allRawOptions,
    [allRawOptions]
  );

  useEffect(() => {
    if (!isOpen || hostOwnsOptions) return;
    if (!shownSelectedSizeCode) return;
    fetchSellerList(shownSelectedSizeCode, shownOptionCode);
  }, [isOpen, hostOwnsOptions, shownSelectedSizeCode, shownOptionCode, fetchSellerList]);

  // Every seller is its own card, so the priceType de-dupe the multiseller list
  // relies on is turned off here.
  const viewModel = useMemo(
    () =>
      mapSlaOptions(rawOptions, priceTypeTags, {
        dedupeByPriceType: false
      }),
    [priceTypeTags, rawOptions]
  );

  /**
   * "Delivery And Return Details Viewed" for the All Sellers screen. No
   * observer: the seller list *is* the screen, so opening it with the list
   * loaded is the view. Each reopen counts again.
   *
   * `other_sellers_available` is false here — every seller for the size is
   * already on this screen, so there is no further list to open.
   */
  const isPdpSurface = surface === SLA_SURFACE.PDP || surface === SLA_SURFACE.SIZE_HALF_CARD;

  /**
   * "Screen Viewed" for the All Sellers screen — fired on open rather than on the
   * list landing: the modal is on show while the sellers are still loading, and
   * an empty or errored list is still a screen the user saw. Each open counts
   * once; `isOpen` going false re-arms it.
   */
  const screenViewedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      screenViewedRef.current = false;
      return;
    }
    if (!EVENT_SURFACES.includes(surface)) return;
    if (screenViewedRef.current) return;
    screenViewedRef.current = true;
    pushAllSellersScreenViewed({
      screenNames: getSlaSurfaceScreenNames(SLA_SURFACE.ALL_SELLERS, {
        openedFrom: surface
      })
    });
  }, [isOpen, surface]);

  useDeliveryDetailsViewed({
    mode: 'open',
    isOpen: Boolean(isOpen),
    // Every host that names its surface reports: PDP, bag popup, wishlist popup.
    enabled: EVENT_SURFACES.includes(surface),
    ready: !isSellerListFetching && Array.isArray(rawOptions) && rawOptions.length > 0,
    resetKeys: [isOpen, activePincode, shownSelectedSizeCode],
    payload: () => {
      // Every seller card on screen, off the same gate — one open, one report,
      // and only once the list has actually loaded.
      pushSellerOptionsAvailableB2c({
        screenNames: getSlaSurfaceScreenNames(SLA_SURFACE.ALL_SELLERS, {
          isDesktop: true,
          openedFrom: surface
        }),
        slaOptions: rawOptions,
        sizeSelected: shownSelectedSizeLabel || hostProduct.sizeName || selectedSizeName,
        productItem: hostProduct.productItem || null,
        productData: isPdpSurface ? productData : null
      });
      pushDeliveryReturnDetailsViewedB2c({
        platform: 'desktop',
        screenNames: getSlaSurfaceScreenNames(SLA_SURFACE.ALL_SELLERS, {
          isDesktop: true,
          openedFrom: surface
        }),
        pincode: activePincode,
        // The strip in here owns the size once the modal is open, so its own
        // chip wins over the label the host opened with (which never moves —
        // hosts that render this modal have closed their popup by then).
        sizeSelected: shownSelectedSizeLabel || hostProduct.sizeName || selectedSizeName,
        sizeSelectedFlag: Boolean(shownSelectedSizeCode),
        slaOptions: rawOptions,
        slaDetailsBySku,
        // This screen carries no delivery note of its own — the config copy
        // belongs to the card behind it, so the field stays empty here.
        deliveryInfoText: undefined,
        otherSellersAvailable: false,
        productItem: hostProduct.productItem || null,
        // Same split as the card behind it: the product slice is only the line
        // on screen on PDP surfaces, so the other hosts pass `productItem`.
        productData: isPdpSurface ? productData : null
      });
    }
  });

  // Whichever card the user is on: their pick, else the API-selected one, else
  // the first serviceable row.
  const activeOption =
    pickedOption ||
    viewModel.options.find(option => option.isSelected && option.serviceable) ||
    viewModel.options.find(option => option.serviceable) ||
    null;

  /**
   * Closing the list (back / X) puts the PDP card back on one seller: the same
   * scopedView call with fetchAllSellers=false, scoped to the selected option.
   * Host-owned card sets keep their own flow — they never opened this from a PDP.
   */
  const handleClose = () => {
    onClose?.();
    if (hostOwnsOptions || !shownSelectedSizeCode) return;
    // Back/X always re-calls scopedView to take the card off the all-sellers
    // view. Only send sellerId/priceGroupId/priceType when the user actually
    // changed the SLA here; with no pick, call scopedView unscoped.
    refetchSelectedSeller(
      shownSelectedSizeCode,
      shownOptionCode,
      pickedOption
        ? {
            priceGroupId: activeOption?.priceGroupId,
            sellerId: activeOption?.sellerId,
            // The API's own priceType only — the view model's is normalised, and
            // defaults to "best" for options that carry none.
            priceType: activeOption?.rawPriceType ?? activeOption?.priceType
          }
        : {}
    );
  };

  const handleSizeSelect = (sizeChip: SizeChip) => {
    if (sizeChip.outOfStock) return;
    // All Sellers screen: the size pick here doesn't refetch scopedView (the
    // list is already every seller for this option), so the event goes out on
    // the click with the sellers on screen.
    const sla = getSelectSizeSlaEventProps(rawOptions || []);
    segmentEventPush({
      event: 'Select Size Clicked',
      half_card_or_pdp: 'PDP',
      page_source: 'PDP Page',
      screen_name: 'all sellers screen',
      selected_size_actual: sizeChip.label,
      selected_size_brand: brandName,
      selected_size_brand_size_chart: '',
      selected_size_universal: '',
      selected_size_universal_size_chart: '',
      size_measurement_type: '',
      tool_tip_content: '',
      urgency_text: '',
      pin_code: activePincode,
      products: [{ ...getProductItemPDP(productData), ...sla.productProps }],
      seller_options: sla.sellerOptions,
      total_sellers_available: sla.totalSellersAvailable
    });
    if (onSelectSize) {
      onSelectSize(sizeChip.code, sizeChip.label);
      // Cart line: size change resets qty to 1, same as the size/qty popup.
      onQuantityChange?.(1);
      return;
    }
    selectSize(sizeChip.label, sizeChip.code, shownOptionCode);
  };

  // Hosts with their own CTA (cart / wishlist popups pass ctaLabel + onAddToBag)
  // keep it — "Go to Bag" is the PDP's state, not theirs.
  const showGoToBag = Boolean(addedToCart) && !ctaLabel && !onAddToBag;
  const goToBagLabel = luxe ? 'GO TO CART' : 'GO TO BAG';

  /**
   * Picking another seller / delivery option means the bag no longer matches
   * what is on screen, so the CTA goes back to "Add to Bag" — same rule the PDP
   * card follows when its selection changes.
   */
  const handleSelectOption = (option: SlaOption) => {
    setPickedOption(option);
    if (!hostOwnsOptions) {
      clearAddedToCart();
    }
    // Cart line: switching seller/SLA resets qty to 1, same as the size/qty popup.
    onQuantityChange?.(1);
  };

  const handleAddToBag = () => {
    if (!shownSelectedSizeCode) return;
    /**
     * Everything the add speaks for comes off one card — the one the user is on
     * (`activeOption`: their pick, else the pre-selected serviceable row, else
     * the first serviceable one). priceType and sellerId were being resolved
     * separately, so a pick whose priceType was blank fell back to another card's
     * priceType while keeping this card's seller — an inconsistent pair for the
     * cart API, and the same pair the event reports.
     *
     * `raw` is the API option behind the card. B2C lists every seller for the
     * size and most of them share a priceType, so nothing else identifies the
     * card, and the raw priceType is what the API expects back (`priceType` on
     * the view model is normalised, and lossy).
     */
    const addedOption = activeOption?.raw || null;
    /**
     * Only the API's own priceType. The view model's `priceType` is not a
     * fallback: `normalizePriceType` defaults anything it does not recognise to
     * "best", empty included, so reading it sent "best" for a card that carries
     * no priceType at all. Blank stays blank and `addToCart` omits the field.
     */
    const selectedPriceType = String(
      addedOption?.priceType ?? activeOption?.rawPriceType ?? ''
    ).trim();
    const addedSellerId =
      addedOption?.sellerId ||
      addedOption?.serviceabilityDetails?.productDetails?.[0]?.sellerId ||
      activeOption?.sellerId ||
      '';
    if (onAddToBag) {
      // The host owns its own add-to-bag flow (events, imsBatchId, quantity), so
      // it only needs the sku and the seller the user settled on.
      onAddToBag(shownSelectedSizeCode, selectedPriceType, addedSellerId, addedOption);
      close();
      return;
    }
    /**
     * This is the only add-to-bag that runs entirely inside this screen — every
     * other host injects its own CTA and its own flow already reports the event,
     * so this is the one place it would otherwise go unreported.
     */
    pushAddToBagClickedB2cAllSellers({
      screenNames: getSlaSurfaceScreenNames(SLA_SURFACE.ALL_SELLERS, {
        isDesktop: true,
        openedFrom: surface
      }),
      slaOptions: rawOptions,
      sizeSelected: shownSelectedSizeLabel || hostProduct.sizeName || selectedSizeName,
      productItem: hostProduct.productItem || null,
      productData: isPdpSurface ? productData : null,
      selectedPriceType,
      // The raw option, not the view model: the event's seller / delivery props
      // come off nested API fields the view model flattens away.
      selectedSlaOption: addedOption
    });
    // B2C: the API needs the seller the picked card belongs to.
    addProductToCart(shownSelectedSizeCode, selectedPriceType, addedSellerId);
    close();
  };

  return (
    <Modal
      name={ModalTypes.B2C_SELLER_LIST}
      className={`${ModalTypes.B2C_SELLER_LIST}${luxe ? ' b2c-seller-list--luxe' : ''}`}
      id={ModalTypes.B2C_SELLER_LIST}
      ariaLabel="Other sellers"
      onClose={handleClose}
    >
      <div
        className={`${styles['sla-modal']}${luxe ? ` ${styles['sla-modal--luxe']}` : ''}`}
      >
        <div className={styles['sla-modal-product']}>
          {shownImageUrl && (
            <img
              src={shownImageUrl}
              alt=""
              aria-hidden="true"
              className={styles['sla-modal-img']}
            />
          )}
          <div className={styles['sla-modal-product-text']}>
            <p className={styles['sla-modal-brand']}>{shownBrandName}</p>
            <p className={styles['sla-modal-name']}>{shownProductName}</p>
          </div>
        </div>

        {/* Same markup and global classes as the PDP size strip
            (components/desktop/product/SizeVariant), so the swatches are the
            PDP's own: `.size-variant-item` for the pill, `.selected` for the
            filled state, `.swatch-size-oos` for the struck-out one. The PDP's
            ids are deliberately not repeated — that block is already on the
            page behind the modal, and ids have to stay unique.

            Luxe uses the wishlist / cart popup chips (`luxe-size-instock`)
            rather than the PDP square `.sizeval` swatches. */}
        {shownSizes.length > 0 && (
          <div
            className={`size-variant-block ${styles['sla-modal-sizes']}`}
            role="radiogroup"
            aria-labelledby={SIZE_LABEL_ID}
          >
            <div className="size-selection" id={SIZE_LABEL_ID}>
              Select Size
            </div>
            {shownSizes.map(sizeChip => {
              const selected = sizeChip.code === shownSelectedSizeCode;
              const sizeStyle = (sizeChip.label || '').length >= 4 ? 'oval' : 'circle';
              return (
                <div className="size-swatch" key={sizeChip.code}>
                  {sizeChip.outOfStock ? (
                    <div
                      className={
                        luxe
                          ? `${sizeStyle} swatch-size-oos size-variant-item luxe-sprite-img`
                          : 'circle swatch-size-oos size-variant-item sprite-img'
                      }
                      role="radio"
                      aria-checked={false}
                      aria-disabled="true"
                      aria-label={sizeChip.label}
                    >
                      {sizeChip.label}
                    </div>
                  ) : (
                    <div
                      className={
                        luxe
                          ? `${sizeStyle} size-variant-item luxe-size-instock${
                              selected ? ' selected' : ''
                            }`
                          : `circle size-variant-item size-instock${selected ? ' selected' : ''}`
                      }
                      role="radio"
                      aria-checked={selected}
                      tabIndex={0}
                      onClick={() => handleSizeSelect(sizeChip)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleSizeSelect(sizeChip);
                        }
                      }}
                    >
                      <span aria-label={sizeChip.label}>{sizeChip.label}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {showQuantity && (
          <>
            <p
              className={styles['sla-modal-heading']}
              role="heading"
              aria-level={2}
              id={QTY_LABEL_ID}
            >
              Select Quantity
            </p>
            <div className={styles['sla-modal-qty']} role="group" aria-labelledby={QTY_LABEL_ID}>
              <button
                type="button"
                className={styles['sla-modal-qty-btn']}
                onClick={() => changeQuantity(pickedQuantity - 1)}
                disabled={pickedQuantity <= minQuantity}
                aria-label="Decrease quantity"
              >
                -
              </button>
              <span
                className={styles['sla-modal-qty-count']}
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {pickedQuantity}
              </span>
              <button
                type="button"
                className={styles['sla-modal-qty-btn']}
                onClick={() => changeQuantity(pickedQuantity + 1)}
                disabled={pickedQuantity >= maxQuantity}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          </>
        )}

        <p className={styles['sla-modal-heading']} role="heading" aria-level={2}>
          {B2C_MOCK_MODAL_TITLE}
        </p>

        <div className={styles['sla-modal-list']}>
          <DeliverySLA
            // This modal lists one card per seller, so the name belongs on the
            // card — unlike the PDP / cart cards, which are delivery choices
            // with a single seller row beneath them.
            showSellerName
            viewModel={viewModel}
            loading={hostOwnsOptions ? false : isSellerListFetching}
            error={hostOwnsOptions ? false : sellerListError}
            activePincode={activePincode}
            hideHeader
            // No delivery note in here — the card on the PDP already carries it.
            footerNote=""
            slaCodeLabels={slaCodeLabels}
            onSelectOption={handleSelectOption}
            className={styles['sla-b2c']}
            luxe={luxe}
          />
        </div>

        {/* Same markup and global classes as the PDP's own CTA
            (components/desktop/product/AddToCart), so it picks up `.btn-gold`
            / `.btn-dark` from the desktop product styles instead of carrying a
            second look. `.btn-gold` is a div there, hence role/tabIndex here —
            the dimmed state is the one thing it has no class for. */}
        {showGoToBag ? (
          // The PDP CTA behind the modal reads "Go to Bag", so this one does too
          // — and goes to the same place.
          <a
            href={appendItmSourceToURL(
              luxe ? '/cart' : `${properties.getProperty('parentUrl')}/cart`
            )}
            className={`${styles['sla-modal-cta']} btn-gold${luxe ? ' btn-dark' : ''}`}
          >
            <span className="ic-pdp-add-cart" />
            <span aria-label={goToBagLabel}>{goToBagLabel}</span>
          </a>
        ) : (
          <div
            className={`${styles['sla-modal-cta']} btn-gold${luxe ? ' btn-dark' : ''}${
              shownSelectedSizeCode ? '' : ` ${styles['sla-modal-cta--disabled']}`
            }`}
            role="button"
            tabIndex={shownSelectedSizeCode ? 0 : -1}
            aria-disabled={!shownSelectedSizeCode}
            onClick={handleAddToBag}
          >
            {/* The cart bag glyph belongs to adding, not to updating a line
                that is already in the bag. */}
            {!ctaLabel && <span className="ic-pdp-add-cart" />}
            <span aria-label={ctaLabel || (luxe ? 'ADD TO CART' : 'ADD TO BAG')}>
              {ctaLabel || (luxe ? 'ADD TO CART' : 'ADD TO BAG')}
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
};

/** Size options live on the FnlSizeVariant base option, with a flat fallback. */
const sizeOptionsOf = (productDetails: any = {}): SizeChip[] => {
  const { baseOptions = [], variantOptions = [] } = productDetails;
  const sizeVariant = baseOptions.find(
    (variant: any) => variant?.variantType === 'FnlSizeVariant'
  );
  const options = sizeVariant?.options?.length ? sizeVariant.options : variantOptions;
  return (options || []).map((option: any) => ({
    code: option?.code,
    label: sizeLabelOf(option),
    outOfStock: option?.stock?.stockLevel === 0 || option?.stock?.stockLevelStatus === 'outOfStock'
  }));
};

/** Card thumbnail — the listing-grid format, same asset the cart popup shows. */
const thumbnailOf = (productDetails: any = {}): string => {
  const { images = [] } = productDetails;
  const match =
    images.find((image: any) => image?.format === 'product') ||
    images.find((image: any) => image?.format === 'productGrid3ListingImage') ||
    images.find((image: any) => image?.format === 'thumbnail');
  return match?.url || '';
};

const mapStateToProps = (state: any, ownProps: OwnProps): StateProps => {
  const productDetails = state?.product?.productDetails || {};
  const b2cSellerConfig = getB2cSellerConfig(state) || {};
  const activePincode = state?.edd?.activePincode;
  // Host-supplied products bring their own sku, and that is the one the seller
  // list is fetched for — read the response back under the same key, or the
  // list would sit empty behind whatever PDP was last visited.
  const selectedSizeCode =
    (ownProps?.product ? ownProps.product.selectedSizeCode : state?.product?.selectedSizeCode) ||
    '';
  return {
    brandName: productDetails?.fnlColorVariantData?.brandName || '',
    productData: getProductData(state),
    productName: productDetails?.name || '',
    imageUrl: thumbnailOf(productDetails),
    optionCode: productDetails?.code || '',
    sizes: sizeOptionsOf(productDetails),
    selectedSizeCode,
    activePincode,
    isOpen: Boolean(state?.modals?.[ModalTypes.B2C_SELLER_LIST]),
    addedToCart: Boolean(state?.popup?.addedToCart),
    priceTypeTags: b2cSellerConfig?.priceTypeTags,
    slaCodeLabels: b2cSellerConfig?.slaTimeMessage,
    selectedSizeName: state?.product?.selectedSizeName || '',
    slaDetailsBySku: (activePincode && state?.edd?.slaDetails?.[activePincode]) || null,
    sellerListOptions: selectSellerListOptions(state, activePincode, selectedSizeCode),
    isSellerListFetching: selectIsSellerListFetching(state, activePincode, selectedSizeCode),
    sellerListError: selectSellerListError(state, activePincode, selectedSizeCode)
  };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  close: () => dispatch(hideModal(ModalTypes.B2C_SELLER_LIST)),
  // "Other sellers available" — the same scopedView call the card makes, asking
  // for every seller instead of the selected one.
  fetchSellerList: (sizeCode, optionCode) =>
    dispatch(fetchSellerListSla(undefined, sizeCode, 1, false, optionCode)),
  refetchSelectedSeller: (sizeCode, optionCode, sellerScope) =>
    dispatch(fetchDeliveryDetailsV2(undefined, sizeCode, 1, false, optionCode, sellerScope)),
  // Same action the PDP size strip dispatches, so the page stays in step with
  // the modal — but with the SLA call skipped: this modal fetches its own seller
  // list for the new size, and re-scopes the card on close. Firing the card's
  // call here would be a second request for a view nobody is looking at.
  selectSize: (sizeName, sizeCode, optionCode) =>
    dispatch(
      selectPdpSize(
        sizeName,
        sizeCode,
        undefined,
        undefined,
        undefined,
        undefined,
        optionCode,
        true
      )
    ),
  clearAddedToCart: () => dispatch(resetAddedToCart()),
  addProductToCart: (sizeCode, selectedPriceType, sellerId) =>
    dispatch(
      addToCart(
        sizeCode,
        'desktop',
        undefined,
        undefined,
        undefined,
        undefined,
        1,
        selectedPriceType,
        sellerId
      )
    )
});

export default connect(mapStateToProps, mapDispatchToProps)(B2cSellerModal);
